import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TimelineEvent,
  TimelineEventAction,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { PiCaretUp, PiCaretDown } from 'react-icons/pi';

export type PortCallListMode = 'vessel' | 'port';

type SortField = 'date' | 'title';
type SortDirection = 'asc' | 'desc';

export type PortCallListViewProps = {
  events: TimelineEvent[];
  mode: PortCallListMode;
  eventActions?: TimelineEventAction[];
  className?: string;
};

export const PortCallListView = ({
  events,
  mode,
  eventActions,
  className,
}: PortCallListViewProps) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedEvents = useMemo(() => {
    const sorted = [...events];
    sorted.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'date') {
        comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
      } else if (sortField === 'title') {
        comparison = a.title.localeCompare(b.title);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [events, sortField, sortDirection]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <PiCaretUp className="inline ml-1 size-3" />
    ) : (
      <PiCaretDown className="inline ml-1 size-3" />
    );
  };

  // Filter only port-call events
  const portCallEvents = useMemo(
    () => sortedEvents.filter((e) => e.type.id === 'port-call'),
    [sortedEvents],
  );

  return (
    <div className={cn('max-h-[500px] overflow-y-auto', className)}>
      <Table container={false}>
        <TableHeader className="sticky top-0 bg-background z-10">
          <TableRow>
            <TableHead className="w-10 text-xs">#</TableHead>
            {mode === 'vessel' ? (
              <>
                <TableHead className="text-xs">LOCODE</TableHead>
                <TableHead
                  className="text-xs cursor-pointer select-none"
                  onClick={() => handleSort('title')}
                >
                  Port Name
                  <SortIcon field="title" />
                </TableHead>
              </>
            ) : (
              <>
                <TableHead
                  className="text-xs cursor-pointer select-none"
                  onClick={() => handleSort('title')}
                >
                  Vessel
                  <SortIcon field="title" />
                </TableHead>
                <TableHead className="text-xs">MMSI</TableHead>
                <TableHead className="text-xs">IMO</TableHead>
                <TableHead className="text-xs">Country</TableHead>
              </>
            )}
            <TableHead
              className="text-xs cursor-pointer select-none"
              onClick={() => handleSort('date')}
            >
              Arrival
              <SortIcon field="date" />
            </TableHead>
            <TableHead className="text-xs">Departure</TableHead>
            <TableHead className="text-xs">Duration</TableHead>
            {eventActions && eventActions.length > 0 && (
              <TableHead className="text-xs w-10" />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {portCallEvents.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={mode === 'vessel' ? 6 : 9}
                className="text-center text-muted-foreground py-8"
              >
                No port calls to display.
              </TableCell>
            </TableRow>
          ) : (
            portCallEvents.map((event, index) => (
              <TableRow key={event.id} className="text-xs">
                <TableCell className="text-muted-foreground">
                  {index + 1}
                </TableCell>
                {mode === 'vessel' ? (
                  <>
                    <TableCell className="font-mono">
                      {event.metadata?.LOCODE || '—'}
                    </TableCell>
                    <TableCell className="font-medium">{event.title}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="font-medium">{event.title}</TableCell>
                    <TableCell className="font-mono">
                      {event.metadata?.MMSI || '—'}
                    </TableCell>
                    <TableCell className="font-mono">
                      {event.metadata?.IMO || '—'}
                    </TableCell>
                    <TableCell>{event.metadata?.Country || '—'}</TableCell>
                  </>
                )}
                <TableCell>{event.metadata?.Arrival || '—'}</TableCell>
                <TableCell>
                  {event.metadata?.Departure === 'N/A' ||
                  !event.metadata?.Departure
                    ? 'In port'
                    : event.metadata.Departure}
                </TableCell>
                <TableCell>
                  {event.metadata?.Duration === 'unknown'
                    ? '—'
                    : event.metadata?.Duration || '—'}
                </TableCell>
                {eventActions && eventActions.length > 0 && (
                  <TableCell>
                    <div className="flex gap-1">
                      {eventActions.map((action) => (
                        <Tooltip key={action.label}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              onClick={() => action.onClick(event)}
                            >
                              {action.icon}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{action.label}</TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
